import React from 'react';
import { useTranslation } from 'react-i18next';
import CustomCard from '../../../../../../../components/CustomCard';
import { CustomTextField } from '../../../../../../../components/CustomTextField';
import { CustomSelect } from '../../../../../../../components/CustomSelect';
import { CustomDatePicker } from '../../../../../../../components/CustomDatePicker';
import ButtonAction from '../../../../../../../components/buttons/ButtonAction';
import ButtonIcon from '../../../../../../../components/buttons/ButtonIcon';
import { AddIcon, TrashOutlineIcon } from '../../../../../../../components/buttons/IconButtons';
import { PERIOD_TIME_UNIT_ENUM } from '../../../../../../../enums/PeriodTimeUnitEnum';
import { USER_DEDUCTION_ENTRY_MODEL, DEDUCTION_CALCULATION_TYPE_MODEL } from '../../DeductionPolicy/Model/Model';
import { toast } from 'react-toastify';
import { validateDecimal } from '../../../../../../../Apis/functions';
import {getCalculationTypeById} from "../../../OvertimePolicies/Global/CreateUpdatePolicy/Model/Model";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 * @Description Step 2 of the Add User Deduction Policy stepper.
 * Allows adding zero or more deduction entries (name, calculation type,
 * value, start date, end date) following the WhiteListManual pattern.
 */
const StepDeductionEntries = ({ deductions, setDeductions, emptyFields, setEmptyFields, disabled }) => {
    const { t } = useTranslation();

    /**
     * Updates a single field in the deduction entry at the given index.
     */
    const handleChange = (index, field, value) => {
        setDeductions(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            if (field === 'calculationType') updated[index].value = '';
            return updated;
        });
    };

    /**
     * Validates current entries and appends a new empty deduction entry.
     */
    const handleAdd = () => {
        const hasInvalid = deductions.some(d => !d.isValid() || !validateDecimal(d.value).isValid || !d.isValidDateRange());
        if (deductions.length > 0 && hasInvalid) {
            setEmptyFields(true);
            toast.warn(t('please_fill_all_fields'));
            return;
        }
        setEmptyFields(false);
        setDeductions(prev => [...prev, { ...USER_DEDUCTION_ENTRY_MODEL, _key: `${Date.now()}-${Math.random()}` }]);
    };

    /**
     * Removes a deduction entry by its temporary _key.
     */
    const handleDelete = (entry) => {
        setDeductions(prev => prev.filter(d => d._key !== entry._key));
    };

    return (
        <div className="col-12 mb-4 mt-3">
            <CustomCard classCard="card-border-8px">
                {deductions.map((entry, index) => {
                    const calculationType = getCalculationTypeById(entry.calculationType);
                    const isDateRangeInvalid = !entry.isValidDateRange();
                    const { isValid: isValidEntryValue, text: isValidEntryText } = validateDecimal(entry.value);
                    return (
                        <div key={entry._key ?? index} className="col-12 p-0 my-2">
                            <div className="border-full-shades-300 background-shades-50 border-radius-8px pt-4">
                                <div className="d-flex align-items-start gap-2 p-2">
                                    <div className="row flex-grow-1">
                                        <div className="col-12 px-1">
                                            <CustomTextField
                                                id={`txt_sde_name_${index}`}
                                                name="name"
                                                value={entry.name}
                                                trans="name"
                                                type="text"
                                                required
                                                length={128}
                                                emptyFields={emptyFields}
                                                disabled={disabled}
                                                onChange={(e) => handleChange(index, 'name', e.target.value)}
                                            />
                                        </div>
                                        <div className="col-12 col-md-6 px-1">
                                            <CustomSelect
                                                id={`slt_sde_calc_type_${index}`}
                                                name="calculationType"
                                                value={entry.calculationType}
                                                keyName="id"
                                                list={DEDUCTION_CALCULATION_TYPE_MODEL()}
                                                trans="calculation_type"
                                                label="calculationType"
                                                required
                                                emptyFields={emptyFields}
                                                showNoSelectionOption={false}
                                                onChange={(e) => handleChange(index, 'calculationType', e.target.value)}
                                                disabled={disabled}
                                            />
                                        </div>
                                        <div className="col-12 col-md-6 px-1">
                                            <CustomTextField
                                                id={`txt_sde_value_${index}`}
                                                name="value"
                                                value={entry.value}
                                                trans="value"
                                                type="text"
                                                required
                                                length={10}
                                                decimals={2}
                                                emptyFields={entry.calculationType ? emptyFields : false}
                                                disabled={disabled? disabled : !entry.calculationType}
                                                suffixText={
                                                    <p className="ibl-font-w-600 text-uppercase">
                                                        {calculationType?.prefixText}
                                                    </p>
                                                }
                                                isValid={entry.calculationType ? isValidEntryValue : true}
                                                isValidText={isValidEntryText || 'this_value_must_be_greater_than_0'}
                                                onChange={(e) => handleChange(index, 'value', e.target.value)}
                                            />
                                        </div>
                                        <div className="col-12 col-md-6 px-1">
                                            <CustomDatePicker
                                                id={`dp_sde_start_${index}`}
                                                name="startDate"
                                                value={entry.startDate}
                                                trans="start_date"
                                                required
                                                emptyFields={emptyFields}
                                                openTo={PERIOD_TIME_UNIT_ENUM.DAYS.key}
                                                onChange={(val) => handleChange(index, 'startDate', val)}
                                                disablePast={true}
                                                disabled={disabled}
                                            />
                                        </div>
                                        <div className="col-12 col-md-6 px-1">
                                            <CustomDatePicker
                                                id={`dp_sde_end_${index}`}
                                                name="endDate"
                                                value={entry.endDate}
                                                trans="end_date"
                                                openTo={PERIOD_TIME_UNIT_ENUM.DAYS.key}
                                                minDate={entry.startDate}
                                                isValid={!isDateRangeInvalid}
                                                isValidText="end_date_cannot_be_before_start_date"
                                                onChange={(val) => handleChange(index, 'endDate', val)}
                                                helperText={t('leave_end_date_empty_if_indefinite')}
                                                disabled={disabled}
                                            />
                                        </div>
                                    </div>
                                    {!disabled && deductions.length > 1 && (
                                        <div className="">
                                            <ButtonIcon
                                                id={`btn_sde_delete_${index}`}
                                                Icon={TrashOutlineIcon}
                                                onClick={() => handleDelete(entry)}
                                                className="btn-outlined-error-600 h-40px w-40px"
                                                iconProps="btn-icon-svg-24px"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}

                { !disabled &&
                <div className="col-12 px-0">
                    <ButtonAction
                        id="btn_sde_add_entry"
                        name="add_deduction_entry"
                        Icon={AddIcon}
                        splitName={false}
                        className="btn-outlined-secondary-600-00"
                        disabled={false}
                        onClick={handleAdd}
                    />
                </div>
                }

            </CustomCard>
        </div>
    );
};

export default StepDeductionEntries;
